import { useState, useEffect, useCallback } from "react";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import type { IUser } from "@/interface/employeeOnBoard";
import type { IPageInfo } from "@/interface/common.interface";
import { ThrowError } from "@/api/callApi";
import { createInitialPageInfo } from "./createSalary.constants";
import {
  buildEmployeeQuery,
  getPageInfoUpdate,
  resetPage,
} from "./createSalary.utils";
import type { UseMasterDataReturn } from "./useMasterData";

export const useEmployeeTab = (masterData: UseMasterDataReturn) => {
  const [employees, setEmployees] = useState<IUser[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState<boolean>(false);
  const [paginationData, setPaginationData] = useState<IPageInfo>(
    createInitialPageInfo,
  );

  const fetchEmployees = useCallback(
    async (
      currentPage = paginationData.currentPage,
      currentLimit = paginationData.pageSize,
    ) => {
      if (!masterData.selectedBranchId && !masterData.selectedSiteId) return;

      setIsLoadingEmployees(true);
      try {
        const response = await EmployeeOnBoardService.getAllEmployees(
          buildEmployeeQuery(
            {
              selectedBranchId: masterData.selectedBranchId,
              selectedSiteId: masterData.selectedSiteId,
              selectedCategory: masterData.selectedCategory,
              searchValue: masterData.searchValue,
            },
            { currentPage, currentLimit },
          ),
        );

        if (response?.status) {
          const userList = response?.data?.user || [];
          setEmployees(userList);
          setPaginationData((prev) => ({
            ...prev,
            ...getPageInfoUpdate(
              response?.data?.pageInfo,
              userList.length,
              currentLimit,
            ),
          }));
        } else {
          setEmployees([]);
          setPaginationData((prev) => ({ ...prev, total: 0, totalPages: 0 }));
        }
      } catch (error) {
        ThrowError(error);
        setEmployees([]);
      } finally {
        setIsLoadingEmployees(false);
      }
    },
    [
      masterData.selectedBranchId,
      masterData.selectedSiteId,
      masterData.selectedCategory,
      masterData.searchValue,
      paginationData.currentPage,
      paginationData.pageSize,
    ],
  );

  // Fetch when tab becomes active
  useEffect(() => {
    if (masterData.activeTab === "employee") fetchEmployees();
  }, [masterData.activeTab, fetchEmployees]);

  const handleChangePageSize = useCallback((newSize: number) => {
    setPaginationData((prev) => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1,
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setPaginationData(resetPage);
  }, []);

  return {
    employees,
    isLoadingEmployees,
    paginationData,
    setPaginationData,
    handleChangePageSize,
    resetPagination,
    refetchEmployees: fetchEmployees,
  };
};

export type UseEmployeeTabReturn = ReturnType<typeof useEmployeeTab>;
